import { connectWithHammock, ENDPOINTS } from 'hammock';
import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { CollectionSelectorToolbar } from './CollectionSelectorToolbar';
import { CollectionSelectorToolbox } from './CollectionSelectorToolbox';
import { CollectionItem, CollectionSelectorProps } from './types';

const CollectionSelectorImpl: React.FC<CollectionSelectorProps> = ({
  context,
  collectionId,
  maxRecords,
  setProps,
  resource,
  onChangeCollectionId,
  onRedirectToCollection,
}: CollectionSelectorProps) => {
  const [records, setRecords] = useState<number>(maxRecords || 20);

  useEffect(() => {
    setRecords(maxRecords || 0);
  }, [maxRecords]);

  const onChangeSource = useCallback(
    (selected: CollectionItem | null) => {
      const oldCollectionId = collectionId;
      setProps({
        collectionId: selected?.value,
      });
      onChangeCollectionId?.(oldCollectionId, selected?.value);
    },
    [onChangeCollectionId, setProps],
  );

  const saveRecords = useCallback(
    debounce((value: number) => {
      setProps({ maxRecords: value });
    }, 300),
    [setProps],
  );

  const onChangeMaxRecords = useCallback(
    (value?: number) => {
      setRecords(value || 0);
      saveRecords(value || 0);
    },
    [saveRecords],
  );

  const items = useMemo(
    () =>
      resource.map(({ collectionName, id }) => ({
        label: collectionName,
        value: id,
      })),
    [resource],
  );

  if (context?.isPopover) {
    return (
      <CollectionSelectorToolbar
        collectionId={collectionId}
        maxRecords={records}
        items={items}
        onChangeSource={onChangeSource}
        onChangeMaxRecords={onChangeMaxRecords}
      />
    );
  }
  return (
    <CollectionSelectorToolbox
      collectionId={collectionId}
      maxRecords={records}
      items={items}
      onChangeSource={onChangeSource}
      onChangeMaxRecords={onChangeMaxRecords}
      onRedirectToCollection={onRedirectToCollection}
    />
  );
};

export const CollectionSelector = connectWithHammock({
  endpoint: ENDPOINTS.collections,
})(CollectionSelectorImpl);
